/**
 * forest.ts — ExtraTrees ensemble.
 *
 * Ported from the `ExtraTreesRegressor` usage in `_proto_reflex_v1.py`
 * (lines 500-523). A forest is N independent ExtraTrees, each with its own
 * per-tree PRNG seed. With `bootstrap=false` (our default) every tree sees
 * the full dataset; the trees differ only in their random threshold draws.
 *
 * Two public surfaces used by the bandit:
 *   - `predictAllActions(X_all)` → per-action (mean, std-across-trees).
 *     The mean is the exploit signal; the std is the exploration signal
 *     (ensemble disagreement — Nilsson et al. TMLR 2024).
 *   - `sampleOneTreePrediction(X_all, treePick)` → one random tree's
 *     prediction per action, used by Thompson Sampling (sample one tree,
 *     act on the sample).
 */

import { ExtraTree, type TreeBuffers } from './tree';
import { makeRng } from './linalg';

export interface ForestHyperparams {
  nTrees: number;
  minSamplesLeaf: number;
  nFeatures: number;
  /** Optional per-tree seed base; the per-tree seed is base + treeIdx. */
  seed: number;
}

/**
 * Mutable ensemble. Re-fit replaces the trees in place.
 *
 * The main-thread agent holds one of these for prediction; when a fit
 * completes in the worker, `loadSerialized` swaps the trees in without
 * blocking the tick loop.
 */
export class ExtraTreesForest {
  readonly hp: ForestHyperparams;
  private trees: ExtraTree[] = [];

  constructor(hp: ForestHyperparams) {
    this.hp = hp;
  }

  get isFit(): boolean {
    return this.trees.length > 0;
  }

  get nTrees(): number {
    return this.trees.length;
  }

  /**
   * Synchronous fit (used by smoketests + the harness in the worker). Each
   * tree gets a distinct seed derived from hp.seed + treeIdx so re-fits are
   * deterministic for a given seed.
   */
  fitSync(X: Float64Array, y: Float64Array): void {
    this.trees = [];
    for (let t = 0; t < this.hp.nTrees; t++) {
      const tree = new ExtraTree(this.hp.nFeatures, this.hp.minSamplesLeaf);
      tree.fit(X, y, makeRng((this.hp.seed + t + 1) >>> 0));
      this.trees.push(tree);
    }
  }

  /**
   * Predict per-action (mean, std-across-trees) for a batch of candidate
   * feature stacks. X_all is laid out as N_ACTIONS × N_FEATURES (row-major).
   *
   * Returns mean/std as Float64Arrays of length N_ACTIONS. The std is the
   * across-tree disagreement at each action — the exploration signal.
   */
  predictAllActions(X_all: Float64Array, nActions: number): {
    mean: Float64Array;
    std: Float64Array;
  } {
    const nFeatures = this.hp.nFeatures;
    const mean = new Float64Array(nActions);
    const std = new Float64Array(nActions);
    if (this.trees.length === 0) {
      mean.fill(0);
      std.fill(1);
      return { mean, std };
    }
    // Per-action accumulator: reuse a scratch buffer of length nTrees.
    const T = this.trees.length;
    const preds = new Float64Array(T);
    for (let a = 0; a < nActions; a++) {
      const row = a * nFeatures;
      // Ask each tree for its prediction on this action's feature row.
      // We pass the full X_all buffer + row offset so no per-action slice
      // is allocated.
      let sum = 0;
      for (let t = 0; t < T; t++) {
        // ExtraTree.predict expects a vector; we give it a view via subarray.
        const p = this.predictTreeOnRow(this.trees[t], X_all, row, nFeatures);
        preds[t] = p;
        sum += p;
      }
      const m = sum / T;
      mean[a] = m;
      let varSum = 0;
      for (let t = 0; t < T; t++) {
        const d = preds[t] - m;
        varSum += d * d;
      }
      std[a] = Math.sqrt(varSum / T);
    }
    return { mean, std };
  }

  /**
   * Route one feature row of X_all down a tree without allocating a subarray.
   * (Same routing as ExtraTree.predict, inlined for the row-offset case.)
   */
  private predictTreeOnRow(
    tree: ExtraTree,
    X_all: Float64Array,
    rowOffset: number,
    nFeatures: number,
  ): number {
    void nFeatures;
    return tree.predictRowOffset(X_all, rowOffset);
  }

  /** Sample one tree's prediction per action (Thompson Sampling). */
  sampleOneTreePrediction(
    X_all: Float64Array,
    nActions: number,
    treePicks: Int32Array,
  ): Float64Array {
    const nFeatures = this.hp.nFeatures;
    const out = new Float64Array(nActions);
    for (let a = 0; a < nActions; a++) {
      const tree = this.trees[treePicks[a]];
      out[a] = tree.predictRowOffset(X_all, a * nFeatures);
    }
    return out;
  }

  /** Serialize all trees (worker → main thread transfer). */
  serialize(): TreeBuffers[] {
    return this.trees.map((t) => t.serialize());
  }

  /** Rehydrate from serialized tree buffers (swap-in from worker). */
  loadSerialized(bufs: TreeBuffers[]): void {
    this.trees = bufs.map((b) => ExtraTree.fromSerialized(b));
  }
}
